import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  Typography,
} from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import MapOutlinedIcon from '@material-ui/icons/MapOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import api from '../../services/api';
import { ROUTES } from '../../constants';

// Onda 2 — sino de alertas da operação, visível em qualquer tela.
// Mesmos alertas do e-mail (B3), com a ação que resolve no clique.

interface IAlert {
  id: number;
  type: string;
  title: string;
  body: string | null;
  severity: 'warn' | 'bad' | null;
  routeId: number | null;
  routeName: string | null;
  driverName: string | null;
  sentAt: string;
}

const SEEN_KEY = 'alertsSeenMaxId';

const timeAgo = (value: string) => {
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(value).getTime()) / 60000),
  );
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  return `há ${hours} h`;
};

export const AlertsBell = () => {
  const [alerts, setAlerts] = useState<IAlert[]>([]);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [seenMaxId, setSeenMaxId] = useState<number>(() =>
    Number(localStorage.getItem(SEEN_KEY) || 0),
  );
  const history = useHistory();
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get('/summarizers/alerts');
      setAlerts(response.data?.alerts || []);
    } catch {
      // API antiga sem o endpoint: sino fica vazio, sem quebrar nada
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  const unseen = alerts.filter((a) => a.id > seenMaxId).length;

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
    const maxId = Math.max(seenMaxId, ...alerts.map((a) => a.id));
    setSeenMaxId(maxId);
    try {
      localStorage.setItem(SEEN_KEY, String(maxId));
    } catch {
      // sem storage, o badge só não persiste
    }
  };

  const openRoute = (alert: IAlert) => {
    if (!alert.routeName) return;
    localStorage.setItem('panelRouteName', alert.routeName);
    setAnchor(null);
    history.push(ROUTES.route);
  };

  const openOrders = () => {
    setAnchor(null);
    history.push(ROUTES.order);
  };

  return (
    <>
      <IconButton color="inherit" onClick={open} aria-label="Alertas da operação">
        <Badge badgeContent={unseen} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ style: { width: 420, maxWidth: '92vw' } }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1.2}
          style={{ borderBottom: '1px solid #e0e4e8' }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            Alertas da operação
          </Typography>
          <Button
            size="small"
            color="primary"
            startIcon={<SettingsOutlinedIcon />}
            onClick={() => {
              setAnchor(null);
              history.push(ROUTES.companyParams);
            }}
          >
            Regras
          </Button>
        </Box>
        {alerts.length === 0 && (
          <Box p={3}>
            <Typography variant="body2" color="textSecondary" align="center">
              Nenhum alerta nas últimas 24h. ✔
              <br />
              As regras e destinatários ficam em Configurações › Parâmetros.
            </Typography>
          </Box>
        )}
        {alerts.map((alert) => (
          <Box
            key={alert.id}
            display="flex"
            px={2}
            py={1.5}
            style={{ borderBottom: '1px solid #eef1f4', gap: 12 }}
          >
            <div
              style={{
                width: 4,
                borderRadius: 4,
                background: alert.severity === 'bad' ? '#C43D3D' : '#B7791F',
                flexShrink: 0,
              }}
            />
            <Box flex={1} minWidth={0}>
              <Typography
                variant="body2"
                style={{ fontWeight: 700, lineHeight: 1.3 }}
              >
                {alert.title}
              </Typography>
              {alert.body && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="div"
                  style={{ margin: '2px 0 6px' }}
                >
                  {alert.body}
                </Typography>
              )}
              <Box display="flex" style={{ gap: 8 }}>
                {alert.routeName && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<MapOutlinedIcon />}
                    onClick={() => openRoute(alert)}
                  >
                    Abrir rota
                  </Button>
                )}
                {alert.type === 'unseparated-due-today' && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<OpenInNewIcon />}
                    onClick={openOrders}
                  >
                    Ver pedidos
                  </Button>
                )}
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ whiteSpace: 'nowrap' }}
            >
              {timeAgo(alert.sentAt)}
            </Typography>
          </Box>
        ))}
      </Popover>
    </>
  );
};
